/**
 * Utilitários para funcionalidade de áudio e síntese de fala
 */

class AudioManager {
  constructor() {
    this.currentUtterance = null;
    this.isPlaying = false;
    this.isPaused = false;
    this.currentText = null;
    this.listeners = new Set();
    this.isInitialized = false;
  }

  /**
   * Inicializa o AudioManager
   */
  init() {
    if (this.isInitialized) return;
    
    // Verifica se a síntese de fala é suportada
    if (!this.isSupported()) {
      console.warn('Web Speech API não é suportada neste navegador');
      return;
    }
    
    this.isInitialized = true;
  }

  /**
   * Adiciona um listener para mudanças de estado
   */
  addListener(callback) {
    if (!this.isInitialized) this.init();
    
    this.listeners.add(callback);
    
    // Retorna função de remoção
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notifica todos os listeners sobre mudanças de estado
   */
  notifyListeners() {
    if (!this.isInitialized) return;
    
    const status = this.getPlayingStatus();
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Erro ao notificar listener:', error);
      }
    });
  }

  /**
   * Verifica se a síntese de fala é suportada pelo navegador
   */
  isSupported() {
    return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  }

  /**
   * Obtém as vozes disponíveis no sistema
   */
  getVoices() {
    if (!this.isInitialized) this.init();
    
    return new Promise((resolve) => {
      let voices = window.speechSynthesis.getVoices();
      
      if (voices.length > 0) {
        resolve(voices);
        return;
      }

      // Aguarda as vozes serem carregadas
      window.speechSynthesis.onvoiceschanged = () => {
        voices = window.speechSynthesis.getVoices();
        resolve(voices);
      };
    });
  }

  /**
   * Encontra uma voz em português brasileiro
   */
  async findPortugueseVoice() {
    const voices = await this.getVoices();
    return voices.find(voice => 
      voice.lang.includes('pt-BR') || 
      voice.lang.includes('pt') ||
      voice.name.toLowerCase().includes('portuguese') ||
      voice.name.toLowerCase().includes('brasil')
    ) || voices[0]; // Fallback para primeira voz disponível
  }

  /**
   * Reproduz texto usando síntese de fala
   */
  async speak(text, options = {}) {
    if (!this.isInitialized) this.init();
    
    if (!this.isSupported()) {
      throw new Error('Síntese de fala não é suportada neste navegador');
    }

    if (!text || text.trim() === '') {
      throw new Error('Texto não pode estar vazio');
    }

    // Para qualquer áudio atual ANTES de criar um novo
    this.stop();

    try {
      const voice = await this.findPortugueseVoice();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Configurações padrão
      utterance.lang = 'pt-BR';
      utterance.rate = options.rate || 1.5;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 0.8;
      utterance.voice = voice;

      // Eventos
      utterance.onstart = () => {
        this.isPlaying = true;
        this.isPaused = false;
        this.currentUtterance = utterance;
        this.currentText = text;
        this.notifyListeners();
        
        if (options.onStart) {
          options.onStart();
        }
      };

      utterance.onend = () => {
        this.isPlaying = false;
        this.isPaused = false;
        this.currentUtterance = null;
        this.currentText = null;
        this.notifyListeners();
        
        if (options.onEnd) {
          options.onEnd();
        }
      };

      utterance.onpause = () => {
        this.isPaused = true;
        this.notifyListeners();
        
        if (options.onPause) {
          options.onPause();
        }
      };

      utterance.onresume = () => {
        this.isPaused = false;
        this.notifyListeners();
        
        if (options.onResume) {
          options.onResume();
        }
      };

      utterance.onerror = (event) => {
        console.warn('AudioManager - Erro na síntese:', event.error);
        
        // Só atualiza o estado se o erro não for de interrupção
        if (event.error !== 'interrupted') {
          this.isPlaying = false;
          this.isPaused = false;
          this.currentUtterance = null;
          this.currentText = null;
          this.notifyListeners();
        }
        
        if (options.onError) {
          options.onError(event);
        }
      };

      // Atualiza o estado ANTES de iniciar a reprodução
      this.isPlaying = true;
      this.isPaused = false;
      this.currentUtterance = utterance;
      this.currentText = text;
      
      // Inicia a reprodução
      window.speechSynthesis.speak(utterance);
      
      // Notifica os listeners imediatamente
      this.notifyListeners();
      
      if (options.onStart) {
        options.onStart();
      }

      return new Promise((resolve, reject) => {
        const originalOnEnd = utterance.onend;
        const originalOnError = utterance.onerror;
        
        utterance.onend = (event) => {
          if (originalOnEnd) originalOnEnd(event);
          resolve();
        };
        
        utterance.onerror = (event) => {
          if (originalOnError) originalOnError(event);
          
          // Só rejeita se o erro não for de interrupção
          if (event.error !== 'interrupted') {
            reject(new Error(`Erro na síntese de fala: ${event.error}`));
          } else {
            // Para interrupções, resolve em vez de rejeitar
            resolve();
          }
        };
      });

    } catch (error) {
      console.error('AudioManager - Erro ao configurar áudio:', error);
      
      // Reset do estado em caso de erro
      this.isPlaying = false;
      this.isPaused = false;
      this.currentUtterance = null;
      this.currentText = null;
      this.notifyListeners();
      
      throw error;
    }
  }

  /**
   * Pausa a reprodução atual
   */
  pause() {
    if (!this.isInitialized) return;
    
    if (this.isPlaying && !this.isPaused) {
      window.speechSynthesis.pause();
      this.isPaused = true;
      this.notifyListeners();
    }
  }

  /**
   * Resume a reprodução pausada
   */
  resume() {
    if (!this.isInitialized) return;
    
    if (this.isPaused) {
      window.speechSynthesis.resume();
      this.isPaused = false;
      this.notifyListeners();
    }
  }

  /**
   * Para completamente a reprodução
   */
  stop() {
    if (!this.isInitialized) return;
    
    if (this.currentUtterance) {
      // Cancela a síntese atual
      window.speechSynthesis.cancel();
      this.currentUtterance = null;
    }
    
    this.isPlaying = false;
    this.isPaused = false;
    this.currentText = null;
    this.notifyListeners();
  }

  /**
   * Verifica se está reproduzindo
   */
  getPlayingStatus() {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      currentText: this.currentText
    };
  }

  /**
   * Limpa recursos quando o componente for desmontado
   */
  cleanup() {
    this.stop();
    this.listeners.clear();
    this.isInitialized = false;
  }
}

// Instância singleton
const audioManager = new AudioManager();

export default audioManager;
